import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from '@phosphor-icons/react';
import { EmptyVault, LoginsCard } from '../../../../../components/deliverables/vault-logins';
import { BrandFilesCard, LinksCard, StorageCard } from '../../../../../components/deliverables/vault-side';
import { Icon } from '../../../../../components/Icon';
import { SearchInput } from '../../../../../components/ui/input';
import { SkeletonRows } from '../../../../../components/ui/skeleton';
import { ErrorState, NoResults } from '../../../../../components/ui/state-views';
import { vaultQuery } from '../../../../../lib/deliverables-api';
import { matchesSearch } from '../../../../../lib/vault-display';

export const Route = createFileRoute('/subdomain/app/_client/projects/deliverables')({
  component: DeliverablesPage,
});

/** Logins on the left, files, links and storage on the right (Figma C03.4: 660 + 397 with a 24px gap). */
const LAYOUT = 'grid items-start gap-(--mbm-space-6) lg:grid-cols-[minmax(0,1fr)_minmax(0,24.8rem)]';

/**
 * Account deliverables (Figma C03.4): what the team shares for the whole business rather than for one task. Logins
 * stay hidden until the person reveals them; files download through signed links.
 */
function DeliverablesPage() {
  const { data: vault, isLoading, isError, refetch } = useQuery(vaultQuery());
  const [text, setText] = useState('');
  const search = text.trim();

  const logins = vault?.logins.filter((i) => matchesSearch(i, text)) ?? [];
  const files = vault?.files.filter((i) => matchesSearch(i, text)) ?? [];
  const links = vault?.links.filter((i) => matchesSearch(i, text)) ?? [];
  const empty = !!vault && vault.logins.length + vault.files.length + vault.links.length === 0;
  const noMatch = !empty && !!vault && logins.length + files.length + links.length === 0;

  return (
    <main className="flex flex-col gap-(--mbm-space-6) px-(--mbm-space-4) py-(--mbm-space-6) lg:gap-(--mbm-space-7) lg:px-(--mbm-space-9) lg:py-(--mbm-space-8)">
      <header className="flex flex-col gap-(--mbm-space-4) sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-(--mbm-space-1)">
          <Link
            to="/subdomain/app/projects"
            search={{ q: undefined }}
            className="inline-flex items-center gap-(--mbm-space-1-5) self-start rounded-(--mbm-radius-xs) pb-(--mbm-space-1-5) text-label-medium text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
          >
            <Icon icon={ArrowLeft} size="sm" />
            Projects
          </Link>
          <h1 className="text-heading-page text-text-strong">Account deliverables</h1>
          <p className="text-body-compact text-text-secondary">
            Shared by your team for the whole business, not a single task
          </p>
        </div>
        <SearchInput
          aria-label="Search logins, files and links"
          tone="white"
          placeholder="Search logins, files, links"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onClear={() => setText('')}
          containerClassName="w-full sm:w-(--mbm-layout-vault-search-width)"
        />
      </header>

      {isError ? (
        <ErrorState
          title="We couldn’t load your deliverables"
          className="rounded-(--mbm-component-radius-widget) bg-surface-card shadow-(--mbm-shadow-table-card)"
          onRetry={() => void refetch()}
        />
      ) : isLoading ? (
        <div className={LAYOUT}>
          <div className="rounded-(--mbm-component-radius-widget) bg-surface-card p-(--mbm-space-6) shadow-(--mbm-shadow-table-card)">
            <SkeletonRows rows={4} label="Loading your logins" />
          </div>
          <div className="rounded-(--mbm-component-radius-widget) bg-surface-card p-(--mbm-space-6) shadow-(--mbm-shadow-table-card)">
            <SkeletonRows rows={3} label="Loading your files" />
          </div>
        </div>
      ) : empty ? (
        <EmptyVault />
      ) : noMatch ? (
        <NoResults
          query={search}
          description="Try a different word, or clear the search."
          onClear={() => setText('')}
          className="rounded-(--mbm-component-radius-widget) bg-surface-card shadow-(--mbm-shadow-table-card)"
        />
      ) : (
        <div className={LAYOUT}>
          {logins.length > 0 ? <LoginsCard logins={logins} /> : <span />}
          <div className="flex flex-col gap-(--mbm-space-4)">
            {files.length > 0 ? <BrandFilesCard files={files} /> : null}
            {links.length > 0 ? <LinksCard links={links} /> : null}
            {vault ? <StorageCard storage={vault.storage} /> : null}
          </div>
        </div>
      )}
    </main>
  );
}
